import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { isNull, mapValues } from 'lodash/fp';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import Select from 'components/shared/select';
import { Icon } from 'ui';
import { updateSchedule } from 'actions/assign';
import { ExpirationDateDisplay } from 'components/PathBuilder/Sidebar/ExpirationSettings';
import { assertAssignDate, assertDueDate } from 'selectors/assign';
import DateInputButton from 'components/shared/DateInputButton';
import GlobalTimeSelect from 'components/shared/GlobalTimeSelect';
import ScrollableContainer from 'components/shared/scrollable';
import GlobalTimeZone from '../../shared/GlobalTimeZone';

class SchedulingView extends PureComponent {
  constructor(props) {
    super(props);
    this.state = props.state;
    this.toggleAssignDate = this.toggleAssignDate.bind(this);
    this.updateAssignDate = this.updateAssignDate.bind(this);
    this.updateAssignTime = this.updateAssignTime.bind(this);
    this.updateAssignTimeZone = this.updateAssignTimeZone.bind(this);
    this.updateDueDate = this.updateDueDate.bind(this);
    this.setDueDateTomorrow = this.setDueDateTomorrow.bind(this);
    this.clearDueDate = this.clearDueDate.bind(this);
    this.toggleRecurring = this.toggleRecurring.bind(this);
    this.updateRecurringOption = this.updateRecurringOption.bind(this);
  }

  componentDidMount() {
    const { hasValidDueDate, hasValidAssignDate } = this.props;
    if (!hasValidDueDate && !hasValidAssignDate) {
      this.setDefaultsOnMount();
    }
  }

  static defaultProps = {
    dateFormat: 'MM/DD/YYYY',
    allowSameDayDueDate: true,
    minAssignDate: moment().add(1, 'days'),
  };

  static defaultOptions = {
    assignDate: null,
    assignTime: '08:00',
    timeZone: 'Eastern Time (US & Canada)',
    dueDate: null,
  };

  /**
   * Assign Date
   */

  setDefaultsOnMount() {
    const { defaultOptions } = SchedulingView;
    this.setState(defaultOptions, () => {
      this.props.updateSchedule(defaultOptions);
    });
  }

  toggleAssignDate(e) {
    const showDueDate = JSON.parse(e.target.value);
    const tomorrowDate = moment().add(1, 'days');
    const defaultDate = this.state.assignDate || tomorrowDate;
    const assignDate = showDueDate ? defaultDate : null;
    this.setState({ assignDate });
    this.updateAssignDate(assignDate);
  }

  updateAssignDate(date) {
    this.setState({ assignDate: date });
    this.props.updateSchedule({
      assignDate: date ? date.toDate() : date,
    });
    // @TODO check for allowSameDayDueDate; it's allowed for now
    const { dueDate } = this.state;
    if (moment(date).isAfter(dueDate, 'days')) {
      this.clearDueDate();
    }
  }

  updateAssignTime(time) {
    this.setState({ assignTime: time });
    this.props.updateSchedule({
      assignTime: time,
    });
  }

  updateAssignTimeZone(newTimeZone) {
    this.setState({ timeZone: newTimeZone });
    this.props.updateSchedule({
      timeZone: newTimeZone,
    });
  }

  /**
   * Due Date
   */

  updateDueDate(date) {
    const dueDate = isNull(date) ? null : date.toDate();
    this.setState({ dueDate });
    this.props.updateSchedule({ dueDate });
  }

  setDueDateTomorrow() {
    this.updateDueDate(moment().add(1, 'days'));
  }

  clearDueDate() {
    this.updateDueDate(null);
  }

  get minDueDate() {
    const { state, props } = this;
    const assignDate = moment(state.assignDate || Date.now());
    if (props.allowSameDayDueDate) {
      return assignDate;
    }
    return assignDate.add(1, 'days');
  }

  get canSelectTomorrow() {
    let { dueDate, assignDate } = this.state; // eslint-disable-line
    const { allowSameDayDueDate } = this.props;

    if (assignDate && !dueDate) {
      const tomorrow = moment().add(1, 'days');
      assignDate = moment(assignDate);

      if (allowSameDayDueDate) {
        return !assignDate.isAfter(tomorrow);
      }

      return !assignDate.isSameOrAfter(tomorrow);
    }
    if (dueDate) {
      return false;
    }

    return true;
  }

  /**
   * Recurring Assignments
   */

  toggleRecurring(e) {
    const recurring = e.target.checked;
    const { recurringOptions: options } = this.state;
    const resetValues = mapValues(() => null);
    const recurringState = {
      recurring,
      recurringOptions: recurring ? options : resetValues(options),
    };
    this.setState(recurringState);
    this.props.updateSchedule(recurringState);
  }

  updateRecurringOption({ target }, key) {
    // disable entering decimals
    if (target.type === 'number' && !/^\d*$/.test(target.value)) return;

    const value = target.value.length ? target.value : null;
    const change = { [key]: value };
    const currentOptions = this.state.recurringOptions;
    this.setState({
      recurringOptions: { ...currentOptions, ...change },
    });
    this.props.updateSchedule({
      recurringOptions: change, // will be merged in updateSchedule
    });
  }

  render() {
    const certifications = this.props.selectedContent.filter(
      content => content.type !== 'Journey' && content?.meta?.isCertification,
    );

    return (
      <div className="AssignModal-detailesView">
        {/* Assignment Date */}
        {
          <div className="FormGroup FormGroup--light">
            <div className="AssignModal-formBlockHeader">
              <h4 className="AssignModal-subtitle llui-text-body-bold">
                Assign Date
              </h4>
            </div>
            <div className="AssignModal-assignDateOption">
              <input
                type="radio"
                name="assignDate"
                id="assignDate-immediately"
                value="false"
                checked={isNull(this.state.assignDate)}
                onChange={this.toggleAssignDate}
              />
              <label
                className="AssignModal-formLabel ml-8"
                htmlFor="assignDate-immediately"
              >
                {t('scheduled_assignments.immediately')}
              </label>
            </div>
            <div className="AssignModal-assignDateOption">
              <input
                type="radio"
                name="assignDate"
                id="assignDate-onDate"
                value="true"
                checked={!!this.state.assignDate}
                onChange={this.toggleAssignDate}
              />
              <label
                className="AssignModal-formLabel ml-8"
                htmlFor="assignDate-onDate"
              >
                {t('scheduled_assignments.date_and_time')}
              </label>
            </div>
            {this.state.assignDate && (
              <div className="d-f fd-r">
                <div className="AssignModal-dateField AssignModal-dateField--indent">
                  <div className="d-f fd-c">
                    <div className="pb-8">
                      {t('scheduled_assignments.date')}
                    </div>
                    <div className="AssignModal-dateField">
                      <DatePicker
                        className="NewInput NewInput--white AssignModal-assingDateInput"
                        selected={moment(this.state.assignDate)}
                        dateFormat={this.props.dateFormat}
                        onChange={this.updateAssignDate}
                        minDate={this.props.minAssignDate}
                        customInput={<DateInputButton />}
                      />
                      <Icon name="calendar" />
                    </div>
                  </div>
                </div>

                <div className="d-f fd-c pr-16">
                  <div className="pb-8">{t('common.time')}</div>
                  <GlobalTimeSelect
                    value={this.state.assignTime}
                    onChange={this.updateAssignTime}
                  />
                </div>

                <div className="d-f fd-c pr-16">
                  <div className="pb-8">{t('common.time_zone')}</div>
                  <GlobalTimeZone
                    timeZones={this.props.timeZones}
                    selectedValue={this.state.timeZone}
                    onChange={this.updateAssignTimeZone}
                  />
                </div>
              </div>
            )}
          </div>
        }

        {/* Due Date */}
        <div className="FormGroup FormGroup--light">
          <div className="AssignModal-formBlockHeader">
            <h4 className="AssignModal-subtitle llui-text-body-bold">
              Due Date (optional)
            </h4>
          </div>
          <div className="AssignModal-dateField">
            <DatePicker
              selected={this.state.dueDate && moment(this.state.dueDate)}
              dateFormat={this.props.dateFormat}
              onChange={this.updateDueDate}
              minDate={this.minDueDate}
              customInput={<DateInputButton />}
            />
            <Icon name="calendar" />
          </div>
          {this.canSelectTomorrow && (
            <DateModifier onClick={this.setDueDateTomorrow}>
              Tomorrow
            </DateModifier>
          )}
          {this.state.dueDate && (
            <DateModifier onClick={this.clearDueDate}>No Due Date</DateModifier>
          )}
        </div>
        {/* Certification Expiration Details */}
        {!!certifications.length && (
          <div className="FormGroup FormGroup--light">
            <div className="AssignModal-formBlockHeader">
              <h4 className="AssignModal-subtitle llui-text-body-bold">
                {t('certifications.expiration')}
              </h4>
            </div>
            <ScrollableContainer maxheight={250}>
              {certifications.map(certification => {
                return (
                  <div key={certification.id}>
                    <div className="llui-text-body">{certification.title}</div>
                    <div className="llui-text-body-light mb-16">
                      <ExpirationDateDisplay
                        certification={certification.meta.certification}
                        format="MM/DD/YYYY"
                        reassignmentNotice={t(
                          'certifications.certification_builder.sidebar.reassignment_notice',
                        )}
                      />
                    </div>
                  </div>
                );
              })}
            </ScrollableContainer>
          </div>
        )}

        {/* Recurring Assignments */}
        {
          <div className="FormGroup FormGroup--light">
            <input
              id="recurring"
              name="recurring"
              type="checkbox"
              onChange={this.toggleRecurring}
              checked={this.state.recurring}
            />
            <label
              className="AssignModal-formLabel ml-8 mb-0"
              htmlFor="recurring"
            >
              Recurring assignment
            </label>
            {this.state.recurring && (
              <div className="AssignModal-recurringOptions">
                <div className="AssignModal-recurringOption">
                  <span className="AssignModal-recurringLabel">
                    Assign every
                  </span>
                  <RecurringAmount
                    onChange={e =>
                      this.updateRecurringOption(e, 'assignOnAmount')
                    }
                    value={this.state.recurringOptions.assignOnAmount || ''}
                  />
                  <RecurringUnit
                    onChange={e =>
                      this.updateRecurringOption(e, 'assignOnUnit')
                    }
                    value={this.state.recurringOptions.assignOnUnit || ''}
                  />
                </div>
                <div className="AssignModal-recurringOption">
                  <span className="AssignModal-recurringLabel">Due within</span>
                  <RecurringAmount
                    onChange={e =>
                      this.updateRecurringOption(e, 'dueDateAmount')
                    }
                    value={this.state.recurringOptions.dueDateAmount || ''}
                  />
                  <RecurringUnit
                    onChange={e => this.updateRecurringOption(e, 'dueDateUnit')}
                    value={this.state.recurringOptions.dueDateUnit || ''}
                  />
                </div>
              </div>
            )}
          </div>
        }
      </div>
    );
  }
}

/**
 * Partials
 */

const DateModifier = props => (
  <button
    {...props}
    type="button"
    className="AssignModal-dateModifier u-linkBtn c-brand-blue"
  />
);

const RecurringAmount = props => (
  <input
    {...props}
    className="NewInput NewInput--white AssignModal-recurringAmount"
    type="number"
    min="1"
    step="1"
  />
);

const RecurringUnit = props => (
  <Select {...props} className="AssignModal-recurringUnit">
    <option value="">–</option>
    <option value="days">Days</option>
    <option value="weeks">Weeks</option>
    <option value="months">Months</option>
    <option value="years">Years</option>
  </Select>
);

/**
 * Redux
 */

const mapStateToProps = (state, ownProps) => ({
  state: state.schedule,
  selectedContent: state.selection.content.data,
  hasValidDueDate: assertDueDate(state),
  hasValidAssignDate: assertAssignDate(state),
});

const mapDispatchToProps = {
  updateSchedule,
};

export default connect(mapStateToProps, mapDispatchToProps)(SchedulingView);
